import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Star as StarIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import tipsService, { Tip } from '@services/tipsService';

const TipDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tip, setTip] = useState<Tip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTip = async () => {
      if (!slug) {
        setError('Slug invalid');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const tipData = await tipsService.getTipBySlug(slug);
        
        if (!tipData) {
          setError('Articolul nu a fost găsit');
          return;
        }

        setTip(tipData);

        // Increment views
        await tipsService.incrementViews(tipData.id);
      } catch (err: any) {
        console.error('Error loading tip:', err);
        setError(err.message || 'Eroare la încărcarea articolului');
      } finally {
        setLoading(false);
      }
    };

    loadTip();
  }, [slug]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !tip) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Articolul nu a fost găsit'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tips')}
          sx={{ mt: 2 }}
        >
          Înapoi la Tips & Tricks
        </Button>
      </Container>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: tip.title,
          text: tip.excerpt,
          url: window.location.href
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      // Fallback: copiază URL-ul în clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiat în clipboard!');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/tips')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Tips & Tricks
        </Link>
        {tip.category_name && (
          <Typography color="text.primary">{tip.category_name}</Typography>
        )}
        <Typography color="text.primary">{tip.title}</Typography>
      </Breadcrumbs>

      {/* Buton înapoi */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/tips')}
        sx={{ mb: 3 }}
      >
        Înapoi
      </Button>

      {/* Header articol */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {tip.category_name && (
                  <Chip
                    label={tip.category_name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {tip.is_featured && (
                  <Chip
                    icon={<StarIcon />}
                    label="Featured"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>

              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                {tip.title}
              </Typography>

              {tip.excerpt && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {tip.excerpt}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  {tip.views_count} vizualizări
                </Typography>
                {tip.published_at && (
                  <Typography variant="caption" color="text.secondary">
                    Publicat: {new Date(tip.published_at).toLocaleDateString('ro-RO')}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handleShare} color="primary">
                <ShareIcon />
              </IconButton>
              <IconButton color="primary">
                <BookmarkIcon />
              </IconButton>
            </Box>
          </Box>

          {tip.cover_image && (
            <Box
              component="img"
              src={tip.cover_image}
              alt={tip.title}
              sx={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'cover',
                borderRadius: 2,
                mb: 3
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Conținut articol */}
      <Card>
        <CardContent>
          <Box
            sx={{
              '& p': { mb: 2 },
              '& h2': { mt: 3, mb: 2, fontWeight: 600 },
              '& h3': { mt: 2, mb: 1, fontWeight: 600 },
              '& ul, & ol': { mb: 2, pl: 3 },
              '& li': { mb: 1 },
              '& img': { maxWidth: '100%', borderRadius: 2, mb: 2 },
              '& blockquote': {
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2,
                py: 1,
                mb: 2,
                fontStyle: 'italic',
                bgcolor: 'background.default'
              }
            }}
            dangerouslySetInnerHTML={{ __html: tip.content }}
          />

          {/* Tags */}
          {tip.tags && tip.tags.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {tip.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Acțiuni */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tips')}
        >
          Înapoi la listă
        </Button>
        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleShare}
        >
          Distribuie
        </Button>
      </Box>
    </Container>
  );
};

export default TipDetailPage;

